/**
 * `AW1` · El proveedor de datos de Refine, hablado contra `admin-web` por HTTP.
 *
 * ## `guard` ① · esta pantalla NO importa `modules/**`
 *
 * Lo único que entra acá del repo son TIPOS de `contracts/read-api`. El grafo de
 * `arquitectura.html` §6 lo dice textual: `web-dev ──✗──▶ cualquier modules/
 * (solo contracts/read-api + admin-api)`.
 *
 * La razón por la que un import de `modules/read-api` sería un error aun cuando
 * compila: `management-ui` es **un artefacto estático en S3 + CloudFront**, no un
 * proceso. `modules/read-api` abre Postgres con `ReadApiDeps.databaseUrl`. Ese
 * import anda en el dev server de quien lo escribe y **no existe en producción**,
 * donde no hay Node que lo ejecute ni credencial de base que usar. Y esa capa es
 * la que sostiene que `admin-web` siga siendo **la única** unidad que ve la PII
 * de ~16.000 menores.
 *
 * **Los imports de abajo son `import type` a propósito, y eso está cableado.**
 * El bundler NO tiene alias hacia `contracts/`: sólo lo tiene TypeScript, en
 * `tsconfig.json`. Un `import type` lo borra `esbuild` antes de resolver nada,
 * así que nunca llega al bundle; pero si alguien algún día importara un VALOR de
 * `contracts/` —por ejemplo `READ_API_ENTRY`, que es la ruta a
 * `modules/read-api/src/index.ts`— el build **falla al resolver el módulo**. La
 * regla de arquitectura queda sostenida por el build y no por la buena memoria.
 */

import type {
  DataProvider,
  GetListParams,
  GetListResponse,
  BaseRecord,
  CrudSort,
} from "@refinedev/core"
import type {
  DocumentListItem,
  ListQuery,
  ListResult,
  SortColumn,
} from "@contracts/read-api/index.ts"
import type { ConfiguracionInstancia } from "./configuracion.ts"
import { mensajeDeError } from "./mensaje-de-error.ts"

/**
 * La fila de la tabla, derivada del contrato — no redeclarada.
 *
 * Es `DocumentListItem` pasado por un tipo mapeado. La traducción existe por una
 * restricción de TypeScript y no por una decisión de diseño: Refine pide
 * `TData extends BaseRecord`, y `BaseRecord` tiene índice de string. Una
 * `interface` —y `DocumentListItem` lo es— **no** recibe índice implícito; un
 * tipo mapeado sí. Redeclarar los campos a mano habría sido la salida cómoda y
 * habría roto lo único que importa: así, cualquier cambio del contrato aparece
 * acá como error de tipos en vez de como una columna que dejó de llenarse.
 */
export type FilaDocumento = { readonly [K in keyof DocumentListItem]: DocumentListItem[K] }

/**
 * El mismo documento **tal como viaja en JSON**.
 *
 * `DocumentListItem.uploadedAt` es un `Date`, y JSON no tiene fechas: por el
 * cable llega un string. Derivarlo del contrato en vez de escribirlo a mano hace
 * que el día que el contrato agregue otra fecha, ésta se convierta sola.
 *
 * **Esto es una decisión que el contrato no tomó, y está en el informe:**
 * `contracts/read-api` es un puerto de TypeScript, no una superficie HTTP — no
 * dice en qué formato viaja la fecha. Acá se asume ISO 8601, que es lo que
 * produce `JSON.stringify` sobre un `Date`.
 */
type DocumentoEnLaRed = {
  readonly [K in keyof DocumentListItem]: DocumentListItem[K] extends Date
    ? string
    : DocumentListItem[K]
}

/** `ListResult` tal como viaja en JSON. */
type ListResultEnLaRed = {
  readonly items: readonly DocumentoEnLaRed[]
  readonly total: number
}

/**
 * El recurso de Refine que corresponde a la tabla de `F1`.
 *
 * **Otra cosa que el contrato no fija y tuve que elegir:** `contracts/read-api`
 * define `list(query)` como método de TypeScript y no dice bajo qué ruta lo
 * expone `admin-web`, ni cómo se llaman los parámetros en el query string. Todo
 * el acuerdo de cable vive en este archivo y en ningún otro, para que cuando
 * `contracts/admin-api` lo fije haya UN lugar que corregir.
 */
export const RECURSO_DOCUMENTOS = "documentos"

/**
 * Las columnas por las que el borde acepta ordenar, como `Record` exhaustivo.
 *
 * Un `Record<SortColumn, …>` y no un arreglo porque el `Record` falla en las dos
 * direcciones: si el contrato AGREGA una columna ordenable, falta una clave; si
 * SACA una, sobra. Un `readonly SortColumn[]` sólo habría detectado la segunda,
 * y la primera —una columna nueva que la pantalla nunca ofrece— es justo la que
 * pasa desapercibida.
 *
 * El valor es la etiqueta visible, así que el `Record` también garantiza que
 * ninguna columna ordenable se quede sin nombre en pantalla.
 */
export const COLUMNAS_ORDENABLES: Record<SortColumn, string> = {
  name: "Nombre del documento",
  uploadedAt: "Fecha de carga",
  state: "Estado",
}

export function esColumnaOrdenable(campo: string): campo is SortColumn {
  return Object.hasOwn(COLUMNAS_ORDENABLES, campo)
}

/**
 * El orden por defecto de la tabla: lo último cargado, primero.
 *
 * El PRD no fija un orden inicial. Se elige `uploadedAt desc` porque el operador
 * que acaba de cargar documentos es el que más mira esta pantalla, y lo que
 * quiere ver es lo que acaba de subir. Está en el informe como elección mía.
 */
export const ORDEN_POR_DEFECTO: SortColumn = "uploadedAt"
export const DIRECCION_POR_DEFECTO: ListQuery["dir"] = "desc"

/**
 * Traduce lo que Refine tiene en la mano a los parámetros del contrato.
 *
 * **`guard` ② vive acá:** el orden y el paginado se PIDEN. Esta función es el
 * único lugar donde se decide qué se le pregunta al borde, y no hay ninguna otra
 * donde se reordene o se recorte lo que contesta.
 *
 * Refine admite ordenar por varias columnas a la vez y `ListQuery` acepta una
 * sola, así que se manda la primera. La pantalla nunca ofrece más de una —los
 * encabezados de `tabla.tsx` reemplazan el orden en vez de apilarlo—, pero el
 * recorte se hace igual acá: si algún día se ofreciera, lo que llegaría al borde
 * sería la primera columna y no un orden que el borde no puede cumplir.
 *
 * Una columna desconocida cae al orden por defecto en vez de viajar tal cual.
 * `SortColumn` es un tipo, no una validación de runtime: mandar `"topic"` haría
 * que `admin-web` conteste un error para una tabla que en pantalla se ve
 * ordenable, y el operador no tendría cómo saber qué pasó.
 */
export function aListQuery(
  pagination: GetListParams["pagination"],
  sorters: readonly CrudSort[] | undefined,
): ListQuery {
  const primero = sorters?.[0]
  const sort = primero !== undefined && esColumnaOrdenable(primero.field)
    ? primero.field
    : ORDEN_POR_DEFECTO
  const dir = primero !== undefined && esColumnaOrdenable(primero.field)
    ? primero.order
    : DIRECCION_POR_DEFECTO

  return {
    sort,
    dir,
    /** Base 1: el contrato lo dice, y es lo que se muestra en la paginación. */
    page: pagination?.currentPage ?? 1,
    pageSize: pagination?.pageSize ?? 10,
  }
}

/**
 * Reconstruye el `Date` que el JSON aplanó a string.
 *
 * Una fecha ilegible se convierte en `Invalid Date`, y `Intl` la muestra como
 * «Invalid Date» sin romper la tabla entera. Se prefiere eso a tirar la
 * respuesta completa: un documento con la fecha mal no debería esconder a los
 * otros diecinueve de la página.
 */
function revivir(documento: DocumentoEnLaRed): FilaDocumento {
  return { ...documento, uploadedAt: new Date(documento.uploadedAt) }
}

/**
 * Error del borde con la forma que Refine espera (`HttpError`).
 *
 * `statusCode` es lo que hace que Refine y `react-query` puedan distinguir «no
 * hay sesión» de «el borde se cayó», que no se manejan igual.
 */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

/**
 * Por qué la ESCRITURA no pasa por acá — actualizado en `AW2`/`AW3`.
 *
 * `DataProvider` de Refine exige `getOne`, `create`, `update` y `deleteOne`
 * aunque este proveedor sólo lea. `AW1` las dejó tirando `501` porque
 * `contracts/admin-api` no existía. **Ahora existe, y estas cuatro siguen
 * tirando `501` igual**, por decisión y no por pendiente: la escritura vive en
 * `borde-escritura.ts`, que es el contrato sin el `actor`.
 *
 * Las tres razones, y las tres son de la forma del contrato:
 *
 *   · `upload()` devuelve `IngestOutcome[]` —UN RESULTADO POR ARCHIVO— y
 *     `create` devuelve **un** registro. Meter el lote ahí obliga a colapsar N
 *     resultados en uno: la implementación exacta que el `guard` ③ de `AW2`
 *     prohíbe.
 *   · `retry()` no es ninguna de las cuatro operaciones CRUD.
 *   · `deleteOne` trae actualización optimista de la caché, y el `guard` ① de
 *     `AW3` la prohíbe: no hay deshacer, así que no hay nada que revertir cuando
 *     el borrado falla.
 *
 * Siguen tirando un error explícito en vez de devolver algo plausible. Un
 * `deleteOne` que resolviera vacío haría que la fila desapareciera de la tabla y
 * volviera cinco segundos después, y el operador leería eso como «el borrado a
 * veces no anda». **Un camino que avisa es honesto; uno que finge que funcionó
 * es un bug de producto.**
 */
/**
 * El texto de abajo lo puede llegar a LEER UN OPERADOR, y por eso no lleva rutas
 * internas.
 *
 * `tabla.tsx` renderiza el `message` de este error en pantalla. Un
 * `Ver docs/_reports/jornada/subagentes/2026-08-08/…` —que es lo que decía antes— es una
 * ruta a un informe de subagente: no es texto para un operador de Elige Educar,
 * y el día que alguien llame a una de estas cuatro por error, lo que aparece en
 * la pantalla es eso. El puntero al informe vive en el comentario de arriba, que
 * es donde lo busca quien tiene que arreglarlo.
 */
function noImplementadaTodavia(operacion: string): never {
  throw errorDelBorde(
    `\`${operacion}\` no pasa por el DataProvider: la escritura vive en \`src/borde-escritura.ts\`, porque la forma de \`contracts/admin-api\` no entra en el CRUD de Refine.`,
    501,
  )
}

/**
 * Arma el proveedor contra la `admin-web` que la instancia configuró.
 *
 * Es una función y no un módulo con estado porque la dirección llega en tiempo
 * de ejecución (ver `configuracion.ts`): no hay ningún momento de import en el
 * que se la pueda conocer.
 */
export function crearProveedorDatos(config: ConfiguracionInstancia): DataProvider {
  const raiz = config.adminWebUrl

  return {
    getApiUrl: () => raiz,

    getList: async <TData extends BaseRecord = BaseRecord>({
      resource,
      pagination,
      sorters,
    }: GetListParams): Promise<GetListResponse<TData>> => {
      const query = aListQuery(pagination, sorters)

      const url = new URL(`${raiz}/${resource}`)
      url.searchParams.set("sort", query.sort)
      url.searchParams.set("dir", query.dir)
      url.searchParams.set("page", String(query.page))
      url.searchParams.set("pageSize", String(query.pageSize))

      let respuesta: Response
      try {
        respuesta = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          /**
           * La sesión la maneja `admin-web` (Better Auth + MFA, ADR-0006) y
           * viaja en una cookie que el navegador guarda y manda solo.
           *
           * `include` y no un `Authorization` armado acá: un token que la
           * pantalla tuviera que leer para poner en un header es un token que la
           * pantalla tuvo que GUARDAR en algún lado, y esta unidad **no guarda
           * nada, ni credenciales ni estado** (`arquitectura.html` §1). Con
           * cookie `HttpOnly` el JavaScript de la pantalla nunca la ve.
           */
          credentials: "include",
        })
      } catch (causa) {
        throw Object.assign(
          errorDelBorde(`No se pudo hablar con el servidor (${raiz}).`, 0),
          { cause: causa },
        )
      }

      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(
            respuesta,
            `El servidor respondió ${respuesta.status} al listar documentos.`,
          ),
          respuesta.status,
        )
      }

      const cuerpo = (await respuesta.json()) as ListResultEnLaRed

      /**
       * `total` sale del borde, SIEMPRE. Nunca `items.length`.
       *
       * Es la mitad de `guard` ② que se puede desarmar sin que ningún examen de
       * `R1` lo note, porque pasa del lado de acá. `ListResult.total` es el total
       * en la BASE; `items.length` es cuántas filas entraron en esta página. Con
       * `items.length` el paginado dice «1 de 1» para siempre, se ve perfecto con
       * veinte documentos y miente con el banco real.
       */
      const resultado: ListResult = {
        items: cuerpo.items.map(revivir),
        total: cuerpo.total,
      }

      /**
       * El único `cast` del archivo. Refine tipa `getList` como genérico sobre
       * `TData` y lo llama con el tipo que pide el consumidor; este proveedor
       * sirve un solo recurso y sabe que es `FilaDocumento`. `tabla.tsx` pide
       * exactamente ese tipo, así que la promesa se cumple en el único lugar
       * desde donde se llama.
       */
      return {
        data: resultado.items,
        total: resultado.total,
      } as unknown as GetListResponse<TData>
    },

    // ── La escritura NO va por acá ────────────────────────────────────────────
    // Vive en `src/borde-escritura.ts`. Ver la nota de `noImplementadaTodavia`.
    getOne: () => noImplementadaTodavia("getOne"),
    create: () => noImplementadaTodavia("create"),
    update: () => noImplementadaTodavia("update"),
    deleteOne: () => noImplementadaTodavia("deleteOne"),
  }
}
